import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon } from 'lucide-react';
import { ProgressIndicator } from './ProgressIndicator';
interface TimelineItem {
  version: string;
  title: string;
  period: string;
  description?: string;
  features: string[];
  notes: string[];
  progress: number;
}
interface TimelineSectionProps {
  data: TimelineItem[];
  isEditMode: boolean;
  onUpdate: (data: any) => void;
  searchQuery?: string;
}
export const TimelineSection = ({
  data,
  isEditMode,
  onUpdate,
  searchQuery = ''
}: TimelineSectionProps) => {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <section className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300" data-id="element-191">
      <div className="bg-gradient-to-r from-[#1A2332] to-[#2A3F5F] py-5 sm:py-6 px-6 sm:px-8" data-id="element-192">
        <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center" data-id="element-193">
          <span className="inline-block h-9 w-9 sm:h-11 sm:w-11 rounded-xl bg-white text-[#1A2332] flex items-center justify-center mr-3 sm:mr-4 text-base sm:text-lg font-bold shadow-md" data-id="element-194">
            4
          </span>
          Timeline
        </h2>
      </div>
      <div className="bg-white dark:bg-[#1A2332] p-6 sm:p-10" data-id="element-195">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-12 sm:space-y-16" data-id="element-196">
          {data.map((item, index) => <motion.div key={index} variants={itemVariants} className="flex flex-col sm:flex-row gap-6" data-id="element-197">
              <div className="flex-shrink-0" data-id="element-198">
                <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl bg-gradient-to-br from-[#7FE5A8] to-[#5FD88F] text-[#1A2332] font-bold text-xl sm:text-2xl flex items-center justify-center shadow-xl border-4 border-white dark:border-[#1A2332]" data-id="element-199">
                  {item.version}
                </div>
              </div>
              <div className="flex-1 bg-white dark:bg-[#2A3F5F] p-5 sm:p-7 rounded-2xl border-2 border-[#7FE5A8] shadow-lg hover:shadow-xl transition-shadow duration-200" data-id="element-200">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 mb-4" data-id="element-201">
                  <div className="flex items-center flex-1" data-id="element-202">
                    <CalendarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-[#1A2332] dark:text-white mr-2 sm:mr-3" data-id="element-203" />
                    <h3 className="text-lg sm:text-xl font-bold text-[#1A2332] dark:text-white" data-id="element-204">
                      {item.title}
                    </h3>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-[#1A2332] dark:text-white bg-[#E8FAF1] dark:bg-[#1A2332] px-3 sm:px-4 py-1.5 rounded-full self-start sm:self-auto" data-id="element-205">
                    {item.period}
                  </span>
                </div>
                <div className="mb-5" data-id="element-206">
                  <ProgressIndicator progress={item.progress} label="Progress" data-id="element-207" />
                </div>
                {item.description && <p className="mb-4 sm:mb-5 text-gray-600 dark:text-gray-300 font-medium text-sm sm:text-base" data-id="element-208">
                    {item.description}
                  </p>}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 sm:mb-5" data-id="element-209">
                  {item.features.map((feature, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: index * 0.1 + idx * 0.05
              }} className="flex items-center text-xs sm:text-sm bg-[#E8FAF1] dark:bg-[#1A2332] p-3 rounded-lg hover:bg-[#d5f5e8] dark:hover:bg-[#0A0F1A] transition-colors" data-id="element-210">
                      <div className="h-6 w-6 sm:h-7 sm:w-7 rounded-lg bg-[#7FE5A8] text-[#1A2332] flex items-center justify-center mr-2 sm:mr-3 text-xs font-bold flex-shrink-0 shadow-sm" data-id="element-211">
                        ✓
                      </div>
                      <span className="font-medium text-[#1A2332] dark:text-white" data-id="element-212">
                        {feature}
                      </span>
                    </motion.div>)}
                </div>
                {item.notes.length > 0 && <div className="space-y-2 text-xs sm:text-sm text-gray-600 dark:text-gray-300" data-id="element-213">
                    {item.notes.map((note, idx) => <p key={idx} className="font-medium" data-id="element-214">
                        • {note}
                      </p>)}
                  </div>}
              </div>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
};